// Iter 31.207 — Agent Dossier (TV-screen mode).
//
// Two surfaces:
//   /agent/:key/dossier      — single agent, full-bleed.
//   /agent/dossier/tv        — auto-cycles every 12s through the roster.
//                              Honours `?agent=key` to land on a specific
//                              agent without auto-cycling (used by the
//                              Chamber Featured Agent card to deep-link
//                              into the matching dossier with TV mode).
//
// Pulls JSON from /api/agents/dossier/index + /api/agents/:key/dossier.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import Shell from "@/components/Shell";
import { Loader2, Download, ChevronLeft, ChevronRight, Pause, Play, Tv2 } from "lucide-react";

const CYCLE_SECS = 12;

function StatLine({ label, value }) {
    return (
        <div className="flex items-baseline gap-3 py-1 border-b border-amber-700/30">
            <span className="text-[10px] tracking-[0.25em] uppercase text-amber-200/60 w-28 flex-shrink-0">
                {label}
            </span>
            <span className="font-mono text-sm text-amber-100 truncate flex-1">
                {value ?? "—"}
            </span>
        </div>
    );
}

function DossierBody({ data, busy }) {
    if (busy || !data) {
        return (
            <div className="flex items-center justify-center h-full text-amber-300 font-mono">
                <Loader2 className="w-6 h-6 animate-spin mr-3" /> LOADING DOSSIER…
            </div>
        );
    }
    const a = data.agent || {};
    const contact = a.contact || {};
    const fin = a.financial || {};
    const op = a.operational || {};
    const career = data.career_history || [];
    const comms = data.comms || {};
    return (
        <div className="grid grid-cols-12 gap-6 p-8 h-full overflow-hidden">
            {/* Left column — photofit + identity */}
            <div className="col-span-4 flex flex-col">
                <div className="border border-amber-600 bg-black/40 aspect-square w-full max-h-[520px] overflow-hidden mb-4">
                    {data.portrait_url ? (
                        <img
                            src={data.portrait_url}
                            alt={a.name || a.key}
                            className="w-full h-full object-cover grayscale contrast-125"
                            data-testid="dossier-portrait"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-amber-400/40 font-mono text-xs tracking-[0.3em] text-center px-6">
                            [ NO PHOTOFIT ON FILE ]
                        </div>
                    )}
                </div>
                <div className="text-amber-200 font-mono">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-amber-200/60">Codename</div>
                    <div className="text-3xl font-bold tracking-wide mb-2" data-testid="dossier-name">
                        {a.name || a.key}
                    </div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-amber-200/60">
                        Spy Finance · Agent {a.number ?? "—"}
                    </div>
                </div>
            </div>

            {/* Right column — data blocks */}
            <div className="col-span-8 overflow-y-auto pr-2 space-y-5 text-amber-100">
                <div>
                    <div className="text-amber-400 text-[11px] tracking-[0.3em] uppercase mb-2 border-b border-amber-600/50 pb-1">
                        ◉ Identity
                    </div>
                    <StatLine label="Alias" value={a.alias} />
                    <StatLine label="Category" value={(a.category || "—").toUpperCase()} />
                    <StatLine label="Status" value={a.status} />
                    <StatLine label="Role" value={a.role} />
                    <StatLine label="Trust class" value={a.trust_class} />
                    <StatLine label="Last used" value={a.last_used} />
                </div>
                <div>
                    <div className="text-amber-400 text-[11px] tracking-[0.3em] uppercase mb-2 border-b border-amber-600/50 pb-1">
                        ◉ Contact
                    </div>
                    <StatLine label="Email" value={contact.email} />
                    <StatLine label="Phone" value={contact.phone} />
                    <StatLine label="Address" value={contact.address} />
                    <StatLine label="Website" value={contact.website} />
                </div>
                <div>
                    <div className="text-amber-400 text-[11px] tracking-[0.3em] uppercase mb-2 border-b border-amber-600/50 pb-1">
                        ◉ Financial
                    </div>
                    <StatLine label="Currency" value={fin.currency} />
                    <StatLine label="Billing alias" value={fin.billing_alias} />
                    <StatLine label="Account ref" value={fin.account_ref} />
                    <StatLine label="VAT" value={fin.vat} />
                </div>
                <div>
                    <div className="text-amber-400 text-[11px] tracking-[0.3em] uppercase mb-2 border-b border-amber-600/50 pb-1">
                        ◉ Operational
                    </div>
                    <StatLine label="Timezone" value={op.timezone} />
                    <StatLine label="Hours" value={op.working_hours} />
                    <StatLine label="Preferred" value={op.preferred_contact} />
                </div>
                <div>
                    <div className="text-amber-400 text-[11px] tracking-[0.3em] uppercase mb-2 border-b border-amber-600/50 pb-1">
                        ◉ Footprint
                    </div>
                    <StatLine label="Inbound comms" value={comms.inbound ?? 0} />
                    <StatLine label="Outbound comms" value={comms.outbound ?? 0} />
                    <StatLine label="Ledger references" value={data.total_history ?? 0} />
                </div>
                <div>
                    <div className="text-amber-400 text-[11px] tracking-[0.3em] uppercase mb-2 border-b border-amber-600/50 pb-1">
                        ◉ Career history — 40 most recent
                    </div>
                    {career.length === 0 && (
                        <div className="text-amber-300/50 font-mono text-xs py-2">
                            No life-entries cross-referenced for this agent yet.
                        </div>
                    )}
                    <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="dossier-career">
                        {career.map((c) => (
                            <div key={c.id} className="grid grid-cols-12 gap-2 text-[11px] font-mono py-1 border-b border-amber-700/20">
                                <span className="col-span-3 text-amber-200/60">
                                    {(c.timestamp || "").slice(0, 16).replace("T", " ")}
                                </span>
                                <span className="col-span-2 text-amber-400 uppercase tracking-wider">
                                    {c.domain}
                                </span>
                                <span className="col-span-7 text-amber-100 truncate">{c.summary}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {a.notes && (
                    <div>
                        <div className="text-amber-400 text-[11px] tracking-[0.3em] uppercase mb-2 border-b border-amber-600/50 pb-1">
                            ◉ Notes
                        </div>
                        <pre className="font-mono text-xs whitespace-pre-wrap text-amber-100/80">{a.notes}</pre>
                    </div>
                )}
            </div>
        </div>
    );
}

function DossierFrame({ data, busy, banner, onPdf }) {
    const a = data?.agent || {};
    return (
        <div
            className="relative w-full h-full bg-gradient-to-b from-[#0a0a0a] via-[#161210] to-[#0a0a0a] text-amber-100 font-mono overflow-hidden"
            data-testid="dossier-frame"
        >
            {/* Banner */}
            <div className="border-b-2 border-amber-600 px-8 py-3 flex items-center justify-between bg-black/40 backdrop-blur-sm">
                <div className="flex items-baseline gap-4">
                    <div className="text-2xl tracking-[0.4em] font-bold text-amber-400">
                        SPY FINANCE
                    </div>
                    <div className="text-[10px] tracking-[0.3em] text-amber-200/60">
                        AGENT DOSSIER · CLASSIFIED · INTERNAL
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {banner}
                    <button
                        onClick={onPdf}
                        disabled={busy || !a.key}
                        data-testid="dossier-pdf-btn"
                        className="inline-flex items-center gap-2 border border-amber-500 text-amber-100 px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase hover:bg-amber-500 hover:text-black disabled:opacity-30"
                    >
                        <Download className="w-3 h-3" /> Download PDF
                    </button>
                </div>
            </div>
            {/* Grid noise overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-10"
                 style={{ backgroundImage: "linear-gradient(0deg, rgba(255,180,80,0.15) 1px, transparent 1px)", backgroundSize: "100% 4px" }} />
            <div className="absolute inset-x-0 top-[60px] bottom-0">
                <DossierBody data={data} busy={busy} />
            </div>
        </div>
    );
}

export default function AgentDossier() {
    const { key } = useParams();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const load = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/agents/${key}/dossier`);
            setData(data);
        } finally {
            setBusy(false);
        }
    }, [key]);
    useEffect(() => { load(); }, [load]);
    const downloadPdf = async () => {
        const resp = await api.get(`/agents/${key}/dossier.pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `spyfinance-dossier-${key}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <Shell active="agents">
            <div className="-mx-6 -my-6 fixed inset-0 top-[60px]" data-testid="dossier-page">
                <DossierFrame
                    data={data}
                    busy={busy}
                    banner={
                        <Link
                            to="/agent/dossier/tv"
                            className="inline-flex items-center gap-2 border border-amber-500/60 text-amber-200 px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase hover:border-amber-300"
                            title="TV cycle mode"
                        >
                            <Tv2 className="w-3 h-3" /> TV mode
                        </Link>
                    }
                    onPdf={downloadPdf}
                />
            </div>
        </Shell>
    );
}

// ---------------------------------------------------------------------------
// TV cycle mode — auto-rotates through every agent.
// ---------------------------------------------------------------------------
export function AgentDossierTV() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const targetKey = searchParams.get("agent");
    const [roster, setRoster] = useState([]);
    const [idx, setIdx] = useState(0);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    // If a target ?agent=key is set, default to paused so the operator
    // lands on the selected agent (not auto-cycling away).
    const [paused, setPaused] = useState(!!targetKey);
    const tickRef = useRef(null);

    // Load roster.
    useEffect(() => {
        (async () => {
            const { data } = await api.get("/agents/dossier/index");
            setRoster(data.agents || []);
        })();
    }, []);

    // Jump to the targetKey once the roster loads.
    useEffect(() => {
        if (!targetKey || roster.length === 0) return;
        const i = roster.findIndex((a) => a.key === targetKey);
        if (i >= 0) setIdx(i);
    }, [targetKey, roster]);

    // Load current dossier.
    useEffect(() => {
        if (!roster.length) return;
        const cur = roster[idx % roster.length];
        if (!cur) return;
        setBusy(true);
        api.get(`/agents/${cur.key}/dossier`)
            .then(({ data }) => setData(data))
            .finally(() => setBusy(false));
    }, [roster, idx]);

    // Auto-cycle.
    useEffect(() => {
        if (paused || roster.length === 0) return undefined;
        tickRef.current = setInterval(() => {
            setIdx((i) => (i + 1) % roster.length);
        }, CYCLE_SECS * 1000);
        return () => clearInterval(tickRef.current);
    }, [paused, roster.length]);

    const cur = roster[idx % Math.max(1, roster.length)];

    const downloadPdf = async () => {
        if (!cur) return;
        const resp = await api.get(`/agents/${cur.key}/dossier.pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `spyfinance-dossier-${cur.key}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black" data-testid="dossier-tv-page">
            <DossierFrame
                data={data}
                busy={busy}
                onPdf={downloadPdf}
                banner={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIdx((i) => (i - 1 + roster.length) % roster.length)}
                            className="border border-amber-500/60 text-amber-200 p-1.5 hover:border-amber-300"
                            data-testid="dossier-tv-prev"
                            aria-label="Previous agent"
                        >
                            <ChevronLeft className="w-3 h-3" />
                        </button>
                        <button
                            onClick={() => setPaused((p) => !p)}
                            className="border border-amber-500/60 text-amber-200 p-1.5 hover:border-amber-300"
                            data-testid="dossier-tv-pause"
                            aria-label={paused ? "Resume cycle" : "Pause cycle"}
                        >
                            {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                        </button>
                        <button
                            onClick={() => setIdx((i) => (i + 1) % roster.length)}
                            className="border border-amber-500/60 text-amber-200 p-1.5 hover:border-amber-300"
                            data-testid="dossier-tv-next"
                            aria-label="Next agent"
                        >
                            <ChevronRight className="w-3 h-3" />
                        </button>
                        <span className="text-[10px] tracking-[0.25em] text-amber-200/60 px-2">
                            {roster.length > 0 ? `${idx + 1} / ${roster.length}` : "— / —"}
                        </span>
                        <button
                            onClick={() => nav("/chamber")}
                            className="border border-amber-500/60 text-amber-200 px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase hover:border-amber-300"
                            data-testid="dossier-tv-exit"
                        >
                            Exit
                        </button>
                    </div>
                }
            />
        </div>
    );
}
